import { z } from 'zod';
import { IsoDateTime, UuidString } from '../../primitives.js';

/**
 * TaskDependency — связь между двумя задачами с типом отношения.
 *
 * Хранится один направление (task_id → related_task_id), reverse-pair вычисляется
 * в read-API (`blocked_by` ↔ `blocking`). Гарантия strict-ordering: task_id ≠ related_task_id.
 *
 * См. `docs/STAITER_UX.md` §11.7 и `docs/backlogs/task-module/be-backlog.md` BE-S12.1-4.
 */

export const DependencyType = z.enum([
  'blocked_by',
  'relates_to',
  'duplicate',
  'start_before',
  'finish_before',
  'implemented_by',
]);
export type DependencyType = z.infer<typeof DependencyType>;

export const TaskDependency = z
  .object({
    id: UuidString,
    entity_id: UuidString,
    task_id: UuidString,
    related_task_id: UuidString,
    relation_type: DependencyType,
    /** BC1 (M2M) — проект связи (D5): в ганте проекта — только его связи.
     *  Nullable — legacy-строки до backfill; `.optional()` — forward-compat parse. */
    project_id: UuidString.nullable().optional(),
    created_at: IsoDateTime,
    created_by_user_id: UuidString,
    /** Denorm-названия сторон ребра (B-6, bundle-паттерн) — для рендера без projectTasks. */
    task_title: z.string().optional(),
    related_task_title: z.string().optional(),
    /** Denorm sequence_id сторон (BE-1, redesign) — FE рендерит «TASK-N» без N+1. */
    task_seq: z.number().int().optional(),
    related_task_seq: z.number().int().optional(),
    /** BC3 — state_group концов (FD6: незакрытый blocked_by-блокер = баннер). */
    task_state_group: z.string().nullable().optional(),
    related_task_state_group: z.string().nullable().optional(),
    /** FE-39 — конец без дат. На ганте стрелка к бездатной задаче приходит к якорю, а не
     *  к бару, и зоны попадания у неё НЕТ: карточка становится единственным полным путём
     *  удаления такой связи, и обязана объяснить, почему её там не «схватить». */
    task_undated: z.boolean().optional(),
    related_task_undated: z.boolean().optional(),
    /**
     * B5 (пак context-menu) — проект КОНЦОВ связи. Не путать с `project_id`
     * выше: там проект самой связи (BC1/D5). Меню чипа задачи ведёт «Открыть
     * проект» и собирает код по этим полям; без них на кросс-проектной связи
     * оно уводило бы в проект связи молча.
     */
    task_project_id: UuidString.nullable().optional(),
    task_project_code: z.string().nullable().optional(),
    related_task_project_id: UuidString.nullable().optional(),
    related_task_project_code: z.string().nullable().optional(),
  })

  .refine((d) => d.task_id !== d.related_task_id, {
    message: 'task_id и related_task_id не могут совпадать (self-reference запрещён)',
    path: ['related_task_id'],
  });
export type TaskDependency = z.infer<typeof TaskDependency>;
